import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

interface StarfieldConfig {
  starCount: Record<'mobile' | 'tablet' | 'desktop', number>;
  starSize: Record<'mobile' | 'tablet' | 'desktop', number>;
  speed: Record<'mobile' | 'tablet' | 'desktop', number>;
  bloom: {
    strength: number;
    radius: number;
    threshold: number;
    resolution: number;
  };
  colors: {
    hueRange: [number, number];
    saturation: number;
    lightness: [number, number];
  };
  depth: number;
  maxPixelRatio: number;
}

const STARFIELD_CONFIG: StarfieldConfig = {
  starCount: {
    mobile: 3000,
    tablet: 5000,
    desktop: 8000
  },
  starSize: {
    mobile: 1.5,
    tablet: 2.0,
    desktop: 2.5
  },
  speed: {
    mobile: 0.8,
    tablet: 1.0,
    desktop: 1.2
  },
  bloom: {
    strength: 0.6,
    radius: 0.5,
    threshold: 0.1,
    resolution: 0.15
  },
  colors: {
    hueRange: [0.55, 0.70],
    saturation: 0.3,
    lightness: [0.7, 1.0]
  },
  depth: 2000,
  maxPixelRatio: 2
};

type ScreenSize = 'mobile' | 'tablet' | 'desktop';

interface OptimalConfig {
  starCount: number;
  starSize: number;
  speed: number;
  screenSize: ScreenSize;
}

function checkWebGLSupport(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!(window.WebGLRenderingContext &&
              (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
  } catch (e) {
    return false;
  }
}

function getOptimalConfig(): OptimalConfig {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const area = width * height;

  let screenSize: ScreenSize;
  if (area < 400000) {
    screenSize = 'mobile';
  } else if (area < 1000000) {
    screenSize = 'tablet';
  } else {
    screenSize = 'desktop';
  }

  const starCount = STARFIELD_CONFIG.starCount[screenSize];
  const starSize = STARFIELD_CONFIG.starSize[screenSize];
  const speed = STARFIELD_CONFIG.speed[screenSize];

  return {
    starCount,
    starSize,
    speed,
    screenSize
  };
}

interface StarfieldOptions {
  starCount?: number;
  speed?: number;
  bloomStrength?: number;
}

export class Starfield {
  private container: HTMLElement;

  private config: OptimalConfig;

  private lastTime = 0;

  private isAnimating = false;

  private scene!: THREE.Scene;

  private camera!: THREE.PerspectiveCamera;

  private renderer!: THREE.WebGLRenderer;

  private stars!: THREE.Points;

  private geometry!: THREE.BufferGeometry;

  private speeds!: Float32Array;

  private starCount!: number;

  private composer!: any;

  private bloomPass!: any;

  private resizeHandler: () => void;

  constructor(container: HTMLElement, options: StarfieldOptions = {}) {
    this.container = container;
    this.config = getOptimalConfig();

    this.config = { ...this.config, ...options };

    this.resizeHandler = this.onResize.bind(this);

    if (!checkWebGLSupport()) {
      console.warn('WebGL not supported, applying fallback background');
      this.applyFallback();
      return;
    }

    try {
      this.init();
    } catch (error) {
      console.error('Starfield initialization failed:', error);
      this.applyFallback();
    }
  }

  private init(): void {
    this.scene = new THREE.Scene();

    const aspect = this.container.clientWidth / this.container.clientHeight;
    this.camera = new THREE.PerspectiveCamera(75, aspect, 0.1, 3000);
    this.camera.position.z = 1000;

    this.renderer = new THREE.WebGLRenderer({
      antialias: false,
      alpha: true,
      powerPreference: 'high-performance'
    });
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, STARFIELD_CONFIG.maxPixelRatio));
    this.container.appendChild(this.renderer.domElement);

    this.createStars();
    this.setupPostProcessing();

    this.onResize();
    window.addEventListener('resize', this.resizeHandler);

    this.renderer.domElement.style.position = 'absolute';
    this.renderer.domElement.style.top = '0';
    this.renderer.domElement.style.left = '0';
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    this.renderer.domElement.style.zIndex = '0';
    this.renderer.domElement.style.pointerEvents = 'none';

    this.isAnimating = true;
    this.animate(0);
  }

  private createStars(): void {
    const starCount = this.config.starCount;
    const positions = new Float32Array(starCount * 3);
    const colors = new Float32Array(starCount * 3);
    this.speeds = new Float32Array(starCount);

    const color = new THREE.Color();

    for (let i = 0; i < starCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 3000;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 3000;
      positions[i * 3 + 2] = Math.random() * STARFIELD_CONFIG.depth - STARFIELD_CONFIG.depth / 2;

      const hueRange = STARFIELD_CONFIG.colors.hueRange;
      const lightnessRange = STARFIELD_CONFIG.colors.lightness;

      const hue = hueRange[0] +
                  Math.random() * (hueRange[1] - hueRange[0]);
      const saturation = STARFIELD_CONFIG.colors.saturation;
      const lightness = lightnessRange[0] +
                       Math.random() * (lightnessRange[1] - lightnessRange[0]);

      color.setHSL(hue, saturation, lightness);
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;

      this.speeds[i] = 0.5 + Math.random() * 1.5;
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    this.starCount = starCount;

    const material = new THREE.PointsMaterial({
      size: this.config.starSize,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      sizeAttenuation: true
    });

    this.stars = new THREE.Points(this.geometry, material);
    this.scene.add(this.stars);
  }

  private setupPostProcessing(): void {
    this.composer = new EffectComposer(this.renderer);

    const renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(renderPass);

    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(
        this.container.clientWidth * STARFIELD_CONFIG.bloom.resolution,
        this.container.clientHeight * STARFIELD_CONFIG.bloom.resolution
      ),
      STARFIELD_CONFIG.bloom.strength,
      STARFIELD_CONFIG.bloom.radius,
      STARFIELD_CONFIG.bloom.threshold
    );
    this.composer.addPass(this.bloomPass);

    const outputPass = new OutputPass();
    this.composer.addPass(outputPass);
  }

  private animateStars(deltaTime: number): void {
    const geometry = this.geometry;
    const speeds = this.speeds;
    const starCount = this.starCount;
    if (!geometry || !speeds || starCount === undefined) return;
    const positionAttr = geometry.attributes.position;
    if (!positionAttr) return;
    const positions = positionAttr.array;
    if (!positions) return;
    const depthHalf = STARFIELD_CONFIG.depth / 2;
    const speedMultiplier = this.config.speed * deltaTime * 60;
    const positionsLen = positions.length;

    for (let i = 0; i < starCount; i++) {
      const speed = speeds[i];
      if (speed === undefined) continue;
      const zIndex = i * 3 + 2;
      if (zIndex >= positionsLen) continue;

      positions[zIndex]! += speed * speedMultiplier;

      if (positions[zIndex]! > depthHalf) {
        positions[zIndex]! = -depthHalf;
      }
    }

    positionAttr.needsUpdate = true;
  }

  private animate(currentTime: number): void {
    if (!this.isAnimating) return;

    requestAnimationFrame(this.animate.bind(this));

    const deltaTime = (currentTime - this.lastTime) / 1000;
    this.lastTime = currentTime;

    if (deltaTime < 0.1 && this.geometry && this.speeds) {
      this.animateStars(deltaTime);
    }

    if (this.composer) {
      this.composer.render();
    }
  }

  private onResize(): void {
    this.camera.aspect = this.container.clientWidth / this.container.clientHeight;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.composer.setSize(this.container.clientWidth, this.container.clientHeight);

    this.bloomPass.resolution.set(
      this.container.clientWidth * STARFIELD_CONFIG.bloom.resolution,
      this.container.clientHeight * STARFIELD_CONFIG.bloom.resolution
    );

    const newConfig = getOptimalConfig();
    if (newConfig.screenSize !== this.config.screenSize) {
      this.adjustStarCount(newConfig);
    }
  }

  private adjustStarCount(newConfig: OptimalConfig): void {
    this.scene.remove(this.stars);
    this.geometry.dispose();
    const material = this.stars.material;
    if (material instanceof THREE.Material) {
      material.dispose();
    }

    this.config = { ...this.config, ...newConfig };

    this.createStars();
  }

  private applyFallback(): void {
    this.container.style.background = 'radial-gradient(ellipse at bottom, #1b2735 0%, #090a0f 100%)';
  }

  dispose(): void {
    this.isAnimating = false;

    window.removeEventListener('resize', this.resizeHandler);

    if (this.stars) {
      this.stars.geometry.dispose();
      const material = this.stars.material;
      if (material instanceof THREE.Material) {
        material.dispose();
      }
    }

    if (this.composer) {
      this.composer.dispose();
    }

    if (this.renderer) {
      this.renderer.dispose();
      if (this.renderer.domElement) {
        this.renderer.domElement.remove();
      }
    }
  }
}
